// Magnus Resolve AI Assistant - Suggested Prompts
// Specialized prompts for Reliability Engineering and FRACAS
// 6 Categories with 42 total prompts aligned to reliability best practices
// ============================================================

import React from 'react';
import {
    Activity,
    AlertTriangle,
    BarChart3,
    CheckCircle,
    Clock,
    DollarSign,
    FileCheck,
    GitBranch,
    Search,
    Target,
    TrendingUp,
    Wrench,
    Shield,
    Network,
    Calendar,
    Package,
    FileText,
    TestTube2,
} from 'lucide-react';

export interface ResolveSuggestedPrompt {
    id: string;
    category: ResolveCategoryType;
    icon: React.ReactNode;
    title: string;
    prompt: string;
    description: string;
}

export type ResolveCategoryType =
    | 'FRACAS & Incident Management'
    | '8D Problem Solving'
    | 'Root Cause Analysis'
    | 'Reliability Analytics'
    | 'Warranty & Cost Management'
    | 'Predictive Maintenance';

export const RESOLVE_CATEGORIES = [
    {
        id: 'FRACAS & Incident Management' as ResolveCategoryType,
        icon: React.createElement(AlertTriangle, { className: "text-red-500" }),
        color: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
        activeColor: 'bg-red-500 text-white',
        description: 'Failure tracking and corrective action'
    },
    {
        id: '8D Problem Solving' as ResolveCategoryType,
        icon: React.createElement(FileCheck, { className: "text-green-500" }),
        color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
        activeColor: 'bg-green-500 text-white',
        description: '8 Disciplines structured methodology'
    },
    {
        id: 'Root Cause Analysis' as ResolveCategoryType,
        icon: React.createElement(Search, { className: "text-purple-500" }),
        color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
        activeColor: 'bg-purple-500 text-white',
        description: '5-Why, Ishikawa, and FTA tools'
    },
    {
        id: 'Reliability Analytics' as ResolveCategoryType,
        icon: React.createElement(BarChart3, { className: "text-blue-500" }),
        color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
        activeColor: 'bg-blue-500 text-white',
        description: 'Weibull, MTBF, and statistical analysis'
    },
    {
        id: 'Warranty & Cost Management' as ResolveCategoryType,
        icon: React.createElement(DollarSign, { className: "text-amber-500" }),
        color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',
        activeColor: 'bg-amber-500 text-white',
        description: 'Cost forecasting and warranty tracking'
    },
    {
        id: 'Predictive Maintenance' as ResolveCategoryType,
        icon: React.createElement(Wrench, { className: "text-teal-500" }),
        color: 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100',
        activeColor: 'bg-teal-500 text-white',
        description: 'PM scheduling and lifecycle planning'
    },
];

export const getResolveSuggestedPrompts = (): ResolveSuggestedPrompt[] => {
    // ========== FRACAS & INCIDENT MANAGEMENT (7 prompts) ==========
    const fracasPrompts: ResolveSuggestedPrompt[] = [
        {
            id: 'fracas-1',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Top Failure Modes',
            prompt: 'Analyze all FRACAS incidents and identify the top 10 failure modes by frequency. Show Pareto analysis with cumulative percentage and recommended focus areas.',
            description: '80/20 analysis on failures'
        },
        {
            id: 'fracas-2',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Failure Rate Trends',
            prompt: 'Show failure rate trends over the last 12 months. Identify any increasing trends that indicate quality degradation or systematic issues requiring investigation.',
            description: 'Detect quality issues early'
        },
        {
            id: 'fracas-3',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Critical Severity Analysis',
            prompt: 'List all Critical and Major severity incidents. For each, show status, root cause assignment, corrective action plan, and time since incident. Flag overdue items.',
            description: 'High-priority incident tracking'
        },
        {
            id: 'fracas-4',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(Clock, { size: 16 }),
            title: 'Time to Resolution Analysis',
            prompt: 'Calculate average time from incident creation to closure. Break down by severity level and identify bottlenecks in the resolution process.',
            description: 'FRACAS process efficiency'
        },
        {
            id: 'fracas-5',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(Package, { size: 16 }),
            title: 'Product/Component Reliability',
            prompt: 'Group incidents by product line or component. Identify which products have the highest failure rates and calculate failure density (failures per 1000 units).',
            description: 'Product quality comparison'
        },
        {
            id: 'fracas-6',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(Calendar, { size: 16 }),
            title: 'Seasonal Pattern Detection',
            prompt: 'Analyze FRACAS data for seasonal patterns. Are certain failure modes correlated with temperature, humidity, or production volume changes?',
            description: 'Environmental correlation'
        },
        {
            id: 'fracas-7',
            category: 'FRACAS & Incident Management',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'RCA Coverage Report',
            prompt: 'Show which FRACAS incidents have linked root cause analysis (5-Why, Ishikawa, FTA). Identify open incidents missing RCA and prioritize which need analysis.',
            description: 'Ensure complete investigation'
        },
    ];

    // ========== 8D PROBLEM SOLVING (7 prompts) ==========
    const eightDPrompts: ResolveSuggestedPrompt[] = [
        {
            id: '8d-1',
            category: '8D Problem Solving',
            icon: React.createElement(FileCheck, { size: 16 }),
            title: '8D Report Status Summary',
            prompt: 'List all active 8D reports with completion status for each discipline (D0-D8). Show which reports are blocked and what steps are overdue.',
            description: 'Overall 8D portfolio health'
        },
        {
            id: '8d-2',
            category: '8D Problem Solving',
            icon: React.createElement(Target, { size: 16 }),
            title: 'D3: Root Cause Validation',
            prompt: 'For all 8D reports at D3 or beyond, validate that the identified root cause is well-supported. Suggest additional verification tests if needed.',
            description: 'Ensure true root causes'
        },
        {
            id: '8d-3',
            category: '8D Problem Solving',
            icon: React.createElement(Shield, { size: 16 }),
            title: 'D2: Containment Effectiveness',
            prompt: 'Review all D2 containment actions. Check if any failures have escaped containment. Calculate containment effectiveness rate and identify gaps.',
            description: 'Verify containment working'
        },
        {
            id: '8d-4',
            category: '8D Problem Solving',
            icon: React.createElement(CheckCircle, { size: 16 }),
            title: 'D5: Corrective Action Validation',
            prompt: 'For completed 8D reports, analyze validation results. Did the corrective actions eliminate the problem? Calculate recurrence rate.',
            description: 'Verify fix effectiveness'
        },
        {
            id: '8d-5',
            category: '8D Problem Solving',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: '8D Cycle Time Analysis',
            prompt: 'Calculate average time spent in each 8D discipline. Identify which steps take longest and recommend process improvements to accelerate closure.',
            description: 'Improve 8D efficiency'
        },
        {
            id: '8d-6',
            category: '8D Problem Solving',
            icon: React.createElement(Network, { size: 16 }),
            title: 'Cross-Functional Team Assessment',
            prompt: 'Analyze 8D team composition. Are the right subject matter experts involved? Recommend additional team members based on problem type.',
            description: 'D0 team optimization'
        },
        {
            id: '8d-7',
            category: '8D Problem Solving',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Recurring Problem Detection',
            prompt: 'Identify similar failure modes across multiple 8D reports. Are we solving the same problem multiple times? Recommend systemic fixes.',
            description: 'Find systemic issues'
        },
    ];

    // ========== ROOT CAUSE ANALYSIS (7 prompts) ==========
    const rcaPrompts: ResolveSuggestedPrompt[] = [
        {
            id: 'rca-1',
            category: 'Root Cause Analysis',
            icon: React.createElement(Search, { size: 16 }),
            title: '5-Why Depth Analysis',
            prompt: 'For all 5-Why analyses, check if the root cause is truly controllable. Identify analyses that may need deeper drilling. Suggest additional "Why?" questions.',
            description: 'Validate root cause depth'
        },
        {
            id: 'rca-2',
            category: 'Root Cause Analysis',
            icon: React.createElement(Network, { size: 16 }),
            title: 'Ishikawa 6M Distribution',
            prompt: 'Analyze Ishikawa diagrams and show distribution of causes across 6M categories (Man, Machine, Material, Method, Measurement, Environment). Identify dominant cause types.',
            description: '6M category breakdown'
        },
        {
            id: 'rca-3',
            category: 'Root Cause Analysis',
            icon: React.createElement(GitBranch, { size: 16 }),
            title: 'FTA Critical Events',
            prompt: 'For all fault trees, identify events with Fussell-Vesely importance >50%. These are critical events that require mandatory controls or design changes.',
            description: 'Prioritize FTA findings'
        },
        {
            id: 'rca-4',
            category: 'Root Cause Analysis',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'RCA Method Effectiveness',
            prompt: 'Compare RCA methods (5-Why vs Ishikawa vs FTA) by problem type. Which method leads to fastest closure and lowest recurrence rates?',
            description: 'Best tool for each problem'
        },
        {
            id: 'rca-5',
            category: 'Root Cause Analysis',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Root Cause Category Analysis',
            prompt: 'Categorize all root causes across FRACAS and 8D. Show top categories (design, process, material, operator, etc.) to guide strategic improvements.',
            description: 'Strategic focus areas'
        },
        {
            id: 'rca-6',
            category: 'Root Cause Analysis',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'Control Recommendation Review',
            prompt: 'For each RCA, review recommended controls (prevention and detection). Ensure controls are SMART: Specific, Measurable, Achievable, Relevant, Time-bound.',
            description: 'Validate control plans'
        },
        {
            id: 'rca-7',
            category: 'Root Cause Analysis',
            icon: React.createElement(Shield, { size: 16 }),
            title: 'Verification Status Tracking',
            prompt: 'Track implementation status of RCA-recommended controls. Show which controls are implemented, in-progress, or pending. Flag overdue items.',
            description: 'Close-the-loop tracking'
        },
    ];

    // ========== RELIABILITY ANALYTICS (8 prompts) ==========
    const analyticsPrompts: ResolveSuggestedPrompt[] = [
        {
            id: 'analytics-1',
            category: 'Reliability Analytics',
            icon: React.createElement(BarChart3, { size: 16 }),
            title: 'Weibull Parameter Analysis',
            prompt: 'Review all Weibull analyses. Identify products with β>3 (rapid wear-out) requiring urgent PM strategy. Show B10 life predictions for all critical components.',
            description: 'Life prediction summary'
        },
        {
            id: 'analytics-2',
            category: 'Reliability Analytics',
            icon: React.createElement(Clock, { size: 16 }),
            title: 'MTBF Trend Monitoring',
            prompt: 'Track MTBF trends over the last 12 months. Calculate if MTBF is improving, stable, or degrading. Identify products below target MTBF.',
            description: 'Fleet reliability tracking'
        },
        {
            id: 'analytics-3',
            category: 'Reliability Analytics',
            icon: React.createElement(Wrench, { size: 16 }),
            title: 'MTTR Optimization Analysis',
            prompt: 'Analyze Mean Time To Repair data. Identify top failure modes with longest repair times. Recommend sparing strategy, training, or design changes to reduce MTTR.',
            description: 'Reduce downtime'
        },
        {
            id: 'analytics-4',
            category: 'Reliability Analytics',
            icon: React.createElement(CheckCircle, { size: 16 }),
            title: 'Availability Scorecard',
            prompt: 'Calculate availability % for all systems. Show which systems meet >99% target. For systems below target, identify if MTBF or MTTR is the primary driver.',
            description: 'Uptime performance'
        },
        {
            id: 'analytics-5',
            category: 'Reliability Analytics',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Bathtub Curve Lifecycle',
            prompt: 'Based on Weibull β parameters, determine lifecycle phase for each product (infant mortality, useful life, or wear-out). Recommend appropriate strategies for each phase.',
            description: 'Lifecycle positioning'
        },
        {
            id: 'analytics-6',
            category: 'Reliability Analytics',
            icon: React.createElement(TestTube2, { size: 16 }),
            title: 'Confidence Interval Analysis',
            prompt: 'Review confidence intervals on all Weibull predictions. Flag analyses with wide confidence bands (R²<0.95) that need more data points.',
            description: 'Statistical validity check'
        },
        {
            id: 'analytics-7',
            category: 'Reliability Analytics',
            icon: React.createElement(GitBranch, { size: 16 }),
            title: 'System-Level Reliability (FTA)',
            prompt: 'Use Fault Tree Analysis to calculate system-level reliability from component failure rates. Identify critical paths and recommend redundancy strategies.',
            description: 'System reliability modeling'
        },
        {
            id: 'analytics-8',
            category: 'Reliability Analytics',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Failure Rate λ(t) Calculation',
            prompt: 'Calculate instantaneous failure rate λ(t) from Weibull parameters. Show how failure rate changes with age. Use for risk-based inspections.',
            description: 'Dynamic failure rate'
        },
    ];

    // ========== WARRANTY & COST MANAGEMENT (7 prompts) ==========
    const warrantyPrompts: ResolveSuggestedPrompt[] = [
        {
            id: 'warranty-1',
            category: 'Warranty & Cost Management',
            icon: React.createElement(DollarSign, { size: 16 }),
            title: '6-Month Cost Forecast',
            prompt: 'Generate warranty cost forecast for next 6 months with 95% confidence intervals. Identify top cost drivers and recommend cost reduction initiatives.',
            description: 'Budget planning forecast'
        },
        {
            id: 'warranty-2',
            category: 'Warranty & Cost Management',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Cost Trend Analysis',
            prompt: 'Analyze warranty cost trends. Is spending increasing or decreasing? Calculate $/unit sold and $/claim metrics. Flag concerning trends.',
            description: 'Cost metrics tracking'
        },
        {
            id: 'warranty-3',
            category: 'Warranty & Cost Management',
            icon: React.createElement(Package, { size: 16 }),
            title: 'Top 10 Costly Failures',
            prompt: 'List top 10 failure modes by total warranty cost. For each, show: claim count, average cost, total cost, and recommended corrective action.',
            description: 'Prioritize by cost impact'
        },
        {
            id: 'warranty-4',
            category: 'Warranty & Cost Management',
            icon: React.createElement(Target, { size: 16 }),
            title: 'Claim Rate Analysis',
            prompt: 'Calculate warranty claim rate (claims per 1000 units sold) for each product. Compare against industry benchmarks and identify products exceeding targets.',
            description: 'Product quality metrics'
        },
        {
            id: 'warranty-5',
            category: 'Warranty & Cost Management',
            icon: React.createElement(Calendar, { size: 16 }),
            title: 'Warranty Period Optimization',
            prompt: 'Based on Weibull B10 life, evaluate if current warranty periods are appropriate. Recommend optimal warranty length to balance customer satisfaction and cost.',
            description: 'Right-size warranty terms'
        },
        {
            id: 'warranty-6',
            category: 'Warranty & Cost Management',
            icon: React.createElement(AlertTriangle, { size: 16 }),
            title: 'Early Failure Detection',
            prompt: 'Identify products with high early-life warranty claims (0-30 days). These indicate quality issues at manufacturing. Recommend burn-in testing or QC improvements.',
            description: 'Manufacturing quality issues'
        },
        {
            id: 'warranty-7',
            category: 'Warranty & Cost Management',
            icon: React.createElement(FileText, { size: 16 }),
            title: 'Cost Breakdown Waterfall',
            prompt: 'Create waterfall chart showing warranty cost components: parts, labor, shipping, admin. Identify which cost categories to target for reduction.',
            description: 'Cost component analysis'
        },
    ];

    // ========== PREDICTIVE MAINTENANCE (6 prompts) ==========
    const pmPrompts: ResolveSuggestedPrompt[] = [
        {
            id: 'pm-1',
            category: 'Predictive Maintenance',
            icon: React.createElement(Wrench, { size: 16 }),
            title: 'PM Schedule Optimization',
            prompt: 'Based on Weibull B10 life, recommend optimal PM intervals (typically 80% of B10). Generate PM schedule for all critical components.',
            description: 'Data-driven PM planning'
        },
        {
            id: 'pm-2',
            category: 'Predictive Maintenance',
            icon: React.createElement(Clock, { size: 16 }),
            title: 'Age-Based Replacement Strategy',
            prompt: 'For components in wear-out phase (β>1), calculate optimal replacement age. Balance maintenance cost vs failure cost to minimize total cost.',
            description: 'Economic replacement interval'
        },
        {
            id: 'pm-3',
            category: 'Predictive Maintenance',
            icon: React.createElement(Activity, { size: 16 }),
            title: 'Condition Monitoring Recommendations',
            prompt: 'For high-value or safety-critical components, recommend condition monitoring parameters (vibration, temperature, oil analysis) based on failure modes.',
            description: 'Predictive vs preventive'
        },
        {
            id: 'pm-4',
            category: 'Predictive Maintenance',
            icon: React.createElement(Target, { size: 16 }),
            title: 'RCM Analysis (Reliability-Centered)',
            prompt: 'Perform criticality analysis on all components. Categorize into: run-to-failure, preventive, predictive, or redesign. Optimize maintenance strategy by category.',
            description: 'Strategic maintenance planning'
        },
        {
            id: 'pm-5',
            category: 'Predictive Maintenance',
            icon: React.createElement(DollarSign, { size: 16 }),
            title: 'Maintenance Cost-Benefit',
            prompt: 'Compare PM cost vs unplanned downtime cost. Calculate ROI of implementing PM programs. Identify components where PM delivers highest value.',
            description: 'Justify PM investment'
        },
        {
            id: 'pm-6',
            category: 'Predictive Maintenance',
            icon: React.createElement(TrendingUp, { size: 16 }),
            title: 'Spares Inventory Optimization',
            prompt: 'Based on MTBF and lead time, calculate optimal spare parts inventory levels. Balance stockout risk vs carrying cost.',
            description: 'Smart spares planning'
        },
    ];

    return [
        ...fracasPrompts,
        ...eightDPrompts,
        ...rcaPrompts,
        ...analyticsPrompts,
        ...warrantyPrompts,
        ...pmPrompts,
    ];
};
